"use client"

import { useState, useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { useAnimatedNumber } from "@/hooks/use-animated-number"
import {
  getConfidenceLabel,
  getConfidenceBarColor,
  getConfidenceLabelColor,
} from "@/lib/confidence-display"

interface ConfidenceMeterProps {
  confidence: number
  className?: string
  showLabel?: boolean
  /**
   * One line under the bar explaining what the number is. Callers pass
   * `CONFIDENCE_SCORE_CAVEAT` so the qualification travels with the score
   * instead of living only in a footer three screens away.
   */
  caveat?: string | null
}

export function ConfidenceMeter({
  confidence,
  className,
  showLabel = true,
  caveat = null
}: ConfidenceMeterProps) {
  const [animated, setAnimated] = useState(false)
  const [showPop, setShowPop] = useState(false)
  const prevConfidenceRef = useRef(0)

  // Use animated number hook for smooth counting
  const animatedConfidence = useAnimatedNumber(
    animated ? confidence : 0,
    1200, // 1.2s duration to match bar animation
    0     // no decimals
  )

  const label = getConfidenceLabel(confidence)
  const barColor = getConfidenceBarColor(confidence)
  const labelColor = getConfidenceLabelColor(confidence)

  // Trigger animation after mount with small delay for smooth entrance
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [confidence])

  // Trigger pop effect when animation completes
  useEffect(() => {
    if (animated && animatedConfidence === confidence && prevConfidenceRef.current !== confidence) {
      setShowPop(true)
      prevConfidenceRef.current = confidence
      const popTimer = setTimeout(() => setShowPop(false), 300)
      return () => clearTimeout(popTimer)
    }
  }, [animated, animatedConfidence, confidence])

  return (
    <div className={cn("space-y-2", className)}>
      {showLabel && (
        // aria-hidden: the count-up animation would otherwise be announced as
        // "0%, 14%, 31%…" The progressbar below carries the final value for AT.
        <div className="flex justify-between items-center text-sm" aria-hidden="true">
          <span className={cn("font-semibold", labelColor)}>
            {label}
          </span>
          <span
            className={cn(
              "text-gray-500 font-medium tabular-nums transition-transform",
              showPop && "animate-number-pop"
            )}
          >
            {animatedConfidence}%
          </span>
        </div>
      )}

      <div
        className="h-2.5 bg-gray-200 rounded-full overflow-hidden relative"
        role="progressbar"
        aria-valuenow={confidence}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Identification confidence: ${confidence}% (${label})`}
      >
        {/* Progress bar with smooth transition (simpler, no layout shift).
            aria-hidden because the live value lives on the parent
            role="progressbar"; the animated count-up text above is decorative
            to AT (it reads "0%" mid-animation otherwise). */}
        <div
          aria-hidden="true"
          className={cn(
            "h-full rounded-full transition-all duration-700 ease-out",
            barColor
          )}
          style={{
            width: animated ? `${confidence}%` : '0%'
          }}
        />

        {/* Subtle glow effect for very high confidence */}
        {animated && confidence >= 85 && (
          <div className="absolute inset-0 bg-green-400/20 rounded-full animate-gentle-pulse" />
        )}

        {/* Completion sparkle for high confidence */}
        {animated && animatedConfidence === confidence && confidence >= 70 && (
          <div
            className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 bg-white rounded-full animate-radiate"
            style={{ right: `${100 - confidence}%` }}
          />
        )}
      </div>

      {caveat && (
        <p className="text-[11px] leading-snug text-gray-500">{caveat}</p>
      )}
    </div>
  )
}
